/**
 * Input-modality tracking for keyboard-activation decisions.
 *
 * `:focus-visible` alone is not a reliable pointer-vs-keyboard signal: some
 * engines keep matching it for controls a pointer click focused, and old
 * Safari throws on the selector entirely. Track which input arrived last — a
 * pointer down or a Tab — and treat the session as pointer-driven until real
 * keyboard navigation happens. In a pointer session, a still-focused
 * actionable control is pointer residue, and Space must blur it instead of
 * letting the browser re-activate it on keyup.
 */
let lastPointerDownAt = -1;
let lastTabAt = -1;

export function notePointerInteraction(): void {
	lastPointerDownAt = Date.now();
}

export function noteKeyboardNavigation(): void {
	lastTabAt = Date.now();
}

function isKeyboardSession(): boolean {
	return lastTabAt >= lastPointerDownAt;
}

/**
 * True when the control was focused through keyboard interaction. A control
 * left focused by a pointer click must not swallow Space into a silent
 * re-activation; keyboard focus keeps native activation.
 */
export function isKeyboardFocusedControl(element: Element): boolean {
	if (!isKeyboardSession()) return false;
	try {
		return element.matches(':focus-visible');
	} catch {
		return true;
	}
}

/** Wire the document-level modality signals; returns the teardown. */
export function attachKeyboardModalityTracking(doc: Document): () => void {
	const onPointerDown = () => notePointerInteraction();
	const onKeyDown = (event: Event) => {
		if ((event as KeyboardEvent).key === 'Tab') noteKeyboardNavigation();
	};
	doc.addEventListener('pointerdown', onPointerDown, true);
	doc.addEventListener('keydown', onKeyDown, true);
	return () => {
		doc.removeEventListener('pointerdown', onPointerDown, true);
		doc.removeEventListener('keydown', onKeyDown, true);
	};
}
